import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home, Bug } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

/**
 * Root-level error boundary that catches unhandled errors
 * and displays a full-page error UI
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
    window.location.href = "/dashboard";
  };

  private handleReload = () => {
    window.location.reload();
  };

  private toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-destructive/10 mx-auto mb-2">
                <AlertTriangle className="h-7 w-7 text-destructive" />
              </div>
              <CardTitle className="text-xl">Something went wrong</CardTitle>
              <CardDescription>
                We encountered an unexpected error. Don't worry, your data is safe.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">Error</p>
                  <p className="font-mono text-xs break-words">{this.state.error.message}</p>
                </div>
              )}

              {this.state.showDetails && this.state.errorInfo && (
                <div className="p-3 bg-muted rounded-lg text-xs font-mono overflow-auto max-h-40">
                  <pre className="whitespace-pre-wrap break-words text-muted-foreground">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={this.handleReload} variant="outline" className="flex-1 gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Reload
                </Button>
                <Button onClick={this.handleReset} className="flex-1 gap-2">
                  <Home className="h-4 w-4" />
                  Dashboard
                </Button>
              </div>

              {this.state.errorInfo && (
                <Button 
                  onClick={this.toggleDetails} 
                  variant="ghost" 
                  size="sm"
                  className="w-full text-xs gap-2"
                >
                  <Bug className="h-3 w-3" />
                  {this.state.showDetails ? "Hide" : "Show"} technical details
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
